import { createDraftSafeSelector } from "@reduxjs/toolkit";
import moment from "moment";
import { budgetDetailsByIdDataSelector } from "@/store/budgets/selectors/details-drawer/budgetDetailsByIdDataSelector";
import { organisationQuarterStartMonthSelector } from "@/store/common/selectors/org-current-settings/organisationQuarterStartMonthSelector";
import { getBudgetDetailsHistoryFormatedDate } from "@/components/pages/budgets-and-alerts/components/drawer/budget-details-drawer/utils/helpers/getBudgetDetailsHistoryFormatedDate";
import { getPlannedBudgetsMap } from "@/components/pages/common/billing-explorer/utils/getPlannedBudgetsMap";
import { getFQuarterByStartMonth } from "@/components/common/date-range-picker/utils/preDefinedRanges/getQuarterlyPreDefinedRange";
import { getIsBudgetDataNotCollected } from "@/store/budgets/utils/helpers/getIsBudgetDataNotCollected";

export type ProcessedHistoryData = {
  id: string;
  used: number | "N/A";
  date: string;
  budget: number;
  formatedDate: string;
  budgetVarianceCostBased: number | "N/A";
  budgetVariancePercentage: number | "N/A";
};

export const budgetDetailsHistoryProcessedDataSelector =
  createDraftSafeSelector(
    [budgetDetailsByIdDataSelector, organisationQuarterStartMonthSelector],
    (budget, quarterStartMonth): ProcessedHistoryData[] | undefined => {
      const {
        period,
        cost_history,
        start_date,
        planned_amounts,
        type,
        amount,
      } = budget ?? {};
      const currentDate = moment();

      if (!cost_history) {
        return;
      }

      const plannedMap = getPlannedBudgetsMap(
        start_date,
        period,
        planned_amounts,
        quarterStartMonth,
      );

      return cost_history.map((item, index) => {
        let formatedDate = getBudgetDetailsHistoryFormatedDate(
          item?.date ?? "",
          period,
          quarterStartMonth,
        );

        if (
          period === "monthly" &&
          moment(item?.date ?? "").month() === currentDate.month() &&
          moment(item?.date ?? "").year() === currentDate.year()
        ) {
          formatedDate = `${formatedDate} (MTD)`;
        }

        const itemDate = moment(item?.date ?? "");
        const { quarter } = getFQuarterByStartMonth(
          itemDate.month() + 1,
          quarterStartMonth,
        );
        const { quarter: currentQuarter } = getFQuarterByStartMonth(
          currentDate.month() + 1,
          quarterStartMonth,
        );

        if (
          period === "quarterly" &&
          quarter === currentQuarter &&
          itemDate.year() === currentDate.year()
        ) {
          formatedDate = `${formatedDate} (QTD)`;
        }

        const { quarter: budgetStartQuarter } = getFQuarterByStartMonth(
          moment(item?.date).month() + 1,
          quarterStartMonth,
        );

        const dateAsNumber =
          period === "monthly"
            ? moment(item?.date).month()
            : budgetStartQuarter;

        const budgetAmount =
          (type === "fixed" ? amount : plannedMap?.[dateAsNumber]) ?? 0;
        const used = item?.used ?? 0;

        const budgetVariancePercentage = (used / budgetAmount - 1) * 100;

        const noCollectedData = getIsBudgetDataNotCollected(
          used,
          index,
          cost_history.length,
          period,
        );

        return {
          used: noCollectedData ? "N/A" : used,
          formatedDate,
          budget: budgetAmount,
          date: item?.date ?? "",
          id: `${budgetAmount}-${used}-${formatedDate}`,
          budgetVariancePercentage: noCollectedData
            ? "N/A"
            : budgetVariancePercentage,
          budgetVarianceCostBased: noCollectedData
            ? "N/A"
            : used - budgetAmount,
        };
      });
    },
  );
